import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus } from "lucide-react";

const num = (v) => parseFloat(v) || 0;
const blank = () => ({ shade: "", meter: "", than: "" });

// Shade grid for a shade-wise product. Shades are typed here, not kept in the Item Master.
export const ShadeDialog = ({ open, onOpenChange, product, pastShades = [], onConfirm, initialRows }) => {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (!open) return;
    const seed = (initialRows || []).map((r) => ({ shade: r.shade || "", meter: r.meter ?? "", than: r.than ?? "" }));
    setRows([...seed, ...Array.from({ length: Math.max(8 - seed.length, 1) }, blank)]);
  }, [open, initialRows]);

  const set = (i, key, value) =>
    setRows((rs) => {
      const next = rs.map((r, idx) => (idx === i ? { ...r, [key]: value } : r));
      return i === rs.length - 1 ? [...next, blank()] : next;
    });

  // Enter walks to the next box, like a voucher screen
  const onGridKey = (e) => {
    if (!["Enter", "ArrowDown", "ArrowUp"].includes(e.key)) return;
    const inputs = Array.from(e.currentTarget.querySelectorAll("input[data-shade-cell]"));
    const at = inputs.indexOf(e.target);
    if (at === -1) return;
    e.preventDefault();
    if (e.key === "Enter") {
      const next = inputs[at + 1];
      if (next) next.focus();
      else setRows((rs) => [...rs, blank()]);
      return;
    }
    const step = e.key === "ArrowDown" ? 3 : -3;
    const target = inputs[at + step];
    if (target) target.focus();
  };

  const totalMeter = rows.reduce((s, r) => s + num(r.meter), 0);
  const totalThan = rows.reduce((s, r) => s + num(r.than), 0);
  const picked = rows.filter((r) => r.shade.trim() && (num(r.meter) || num(r.than)));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="shade-dialog" className="max-h-[92vh] max-w-2xl overflow-y-auto rounded-sm">
        <DialogHeader>
          <DialogTitle className="font-head text-xl font-extrabold tracking-tighter">{product}</DialogTitle>
          <DialogDescription className="text-xs text-zinc-500" data-testid="shade-dialog-subtitle">
            Qty: <span className="font-mono-num font-semibold text-zinc-700">{totalMeter.toFixed(2)} METRE</span>{" "}
            ( <span className="font-mono-num font-semibold text-zinc-700">{totalThan.toFixed(2)} THAN</span> ) — type
            each shade with its meter and than. The same shade can be entered more than once.
          </DialogDescription>
        </DialogHeader>

        <datalist id="shade-suggestions">
          {pastShades.map((s, i) => (
            <option key={`${s.shade}-${i}`} value={s.shade} />
          ))}
        </datalist>

        <div className="border border-zinc-200">
          <div className="grid grid-cols-[40px_1fr_110px_110px] gap-2 bg-zinc-50 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
            <span>S no</span>
            <span>Shade</span>
            <span className="text-right">Qty (Meter)</span>
            <span className="text-right">Alt Qty (Than)</span>
          </div>
          <div className="max-h-[45vh] overflow-y-auto" onKeyDown={onGridKey}>
            {rows.map((r, i) => (
              <div key={i} className="grid grid-cols-[40px_1fr_110px_110px] items-center gap-2 border-t border-zinc-200 px-3 py-1.5">
                <span className="font-mono-num text-xs text-zinc-400">{i + 1}</span>
                <Input
                  data-testid={`shade-input-${i}`}
                  data-shade-cell="shade"
                  list="shade-suggestions"
                  className="h-8 rounded-sm"
                  placeholder="Type shade"
                  value={r.shade}
                  onChange={(e) => set(i, "shade", e.target.value)}
                />
                <Input
                  data-testid={`shade-meter-${i}`}
                  data-shade-cell="meter"
                  type="number"
                  step="any"
                  className="h-8 rounded-sm text-right"
                  value={r.meter}
                  onChange={(e) => set(i, "meter", e.target.value)}
                />
                <Input
                  data-testid={`shade-than-${i}`}
                  data-shade-cell="than"
                  type="number"
                  step="any"
                  className="h-8 rounded-sm text-right"
                  value={r.than}
                  onChange={(e) => set(i, "than", e.target.value)}
                />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-[40px_1fr_110px_110px] gap-2 border-t border-zinc-300 bg-zinc-50 px-3 py-2 text-sm font-semibold">
            <span />
            <span>Total</span>
            <span className="font-mono-num text-right" data-testid="shade-total-meter">{totalMeter.toFixed(2)}</span>
            <span className="font-mono-num text-right" data-testid="shade-total-than">{totalThan.toFixed(2)}</span>
          </div>
        </div>

        <div className="flex items-center justify-between gap-2">
          <Button type="button" variant="outline" size="sm" className="rounded-sm" data-testid="shade-add-row-button" onClick={() => setRows((rs) => [...rs, blank()])}>
            <Plus size={14} className="mr-1.5" /> Add row
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="outline" className="rounded-sm" data-testid="shade-cancel-button" onClick={() => onOpenChange(false)}>
              Quit
            </Button>
            <Button
              type="button"
              data-testid="shade-confirm-button"
              className="rounded-sm bg-[var(--mod-primary)] text-white hover:opacity-90"
              disabled={picked.length === 0}
              onClick={() => {
                onConfirm(picked.map((r) => ({ shade: r.shade.trim(), meter: num(r.meter), than: num(r.than) })));
                onOpenChange(false);
              }}
            >
              OK — {picked.length} row(s)
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
